import React, { FC, useEffect, useState } from 'react';
import { Button, Form, Input, Modal, Select, Space, Switch } from 'antd';
import { FormWrapper } from 'common/components/form-wrapper/form-wrapper';
import { ReactComponent as SaveOutlined } from 'assets/icons/save.svg';
import { IModal } from 'common/models/model.interface';
import { observer } from 'mobx-react-lite';
import configurationService from 'common/services/configuration-service/configuration.service';
import { directionOptions, entryTypeOptions } from 'features/eiq-secure/constans/common.eiq-secure';
import { PlusOutlined } from '@ant-design/icons';
import { ReactComponent as Trash } from 'assets/icons/trash.svg';
import './edit-gate-midal.scss';

const AddEditGateModal: FC<IModal<any, any>> = observer(({ title, isEdit, initData, isOpen, onClose }) => {
  const [form] = Form.useForm();
  const [showRtsp, setShowRtsp] = useState(false);

  useEffect(() => {
    if (form && isOpen) {
      form.setFieldsValue(initData);
      setShowRtsp(initData.useRtsp);
    }
  }, [isOpen, form, initData]);

  const hideModal = (...rest: any) => {
    form.resetFields();
    if (onClose) {
      onClose(...rest);
    }
  };

  const onFinish = async (values: any) => {
    const model: any = {
      useRtsp: values.useRtsp,
      rtspUrl: values.useRtsp ? values.rtspUrl : '',
      useGateController: values.useGateController,
      longitude: values.longitude ? values.longitude : null,
      latitude: values.latitude ? values.latitude : null,
      entryType: values.entryType ? values.entryType : null,
      panelNumber: values.panelNumber ? values.panelNumber : null,
      cameras: values.cameras,
      readers: values.readers,
      relays: values.relays,
    };

    if (isEdit) {
      model.name = initData.name;
      model.newName = values.name;
      const result = await configurationService.updateGateConfiguraion(model);
      hideModal(true, result);
    } else {
      model.name = values.name;
      await configurationService.createGateConfiguraion(model);
      hideModal(true);
    }
  };

  return (
    <Modal centered title={title} width={550} open={isOpen} wrapClassName="custom-modal" closable={false}>
      <FormWrapper form={form} initialValues={initData} onFinish={onFinish} layout="vertical">
        <Form.Item name="name" label="Location (Gate) Name" rules={[{ required: true, message: 'Location (Gate) name is required!' }]}>
          <Input placeholder="Enter location (gate) name" />
        </Form.Item>
        <Form.Item name="longitude" label="Longitude">
          <Input placeholder="Enter gate longitude" type="number" max={180} min={-180} step="any" />
        </Form.Item>
        <Form.Item name="latitude" label="Latitude">
          <Input placeholder="Enter gate latitude" type="number" max={90} min={-90} step="any" />
        </Form.Item>
        <Form.Item name="panelNumber" label="Card Access Panel Number">
          <Input placeholder="Enter card access panel number" />
        </Form.Item>
        <Form.Item name="entryType" label="Gate entry type">
          <Select placeholder="Select gate entry type" options={entryTypeOptions} allowClear={true} />
        </Form.Item>
        <Form.Item name="useRtsp" label="Use RTSP Camera" valuePropName="checked">
          <Switch onChange={(checked: boolean) => setShowRtsp(checked)} />
        </Form.Item>
        {showRtsp && (
          <Form.Item name="rtspUrl" label="RTSP Url" rules={[{ required: true, message: 'RTSP Url is required!' }]}>
            <Input placeholder="Enter RTSP Url" />
          </Form.Item>
        )}
        <Form.Item name="useGateController" label="Enable gate controller" valuePropName="checked">
          <Switch />
        </Form.Item>
        <div className="edit-gate-form-list-wrapper">
          <h1 className="h1">Gate cameras</h1>
          <Form.List name="cameras">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div className="edit-gate-sub-list" key={key}>
                    <Form.Item {...restField} name={[name, 'name']} label="Camera name" rules={addEditGateRules.cameraName}>
                      <Input placeholder="Enter camera name" />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'direction']} label="Camera direction" rules={addEditGateRules.cameraDirection}>
                      <Select placeholder="Select camera direction" options={directionOptions} />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'isGuestLine']} label="Is guest line?" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                    <Button
                      htmlType="button"
                      style={{ width: '100%' }}
                      icon={<Trash className="trach-icon" />}
                      onClick={() => remove(name)}
                      danger>
                      Remove
                    </Button>
                  </div>
                ))}
                <Button htmlType="button" icon={<PlusOutlined />} onClick={() => add()} style={{ width: '100%' }}>
                  Add camera
                </Button>
              </>
            )}
          </Form.List>
        </div>
        <div className="edit-gate-form-list-wrapper">
          <h1 className="h1">Gate readers</h1>
          <Form.List name="readers">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div className="edit-gate-sub-list" key={key}>
                    <Form.Item {...restField} name={[name, 'number']} label="Reader number" rules={addEditGateRules.readerNumber}>
                      <Input placeholder="Enter reader number" />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'direction']} label="Reader direction" rules={addEditGateRules.readerDirection}>
                      <Select placeholder="Select reader direction" options={directionOptions} />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'entryType']} label="Reader entry type" rules={addEditGateRules.readerEntryType}>
                      <Select placeholder="Select reader entry type" options={entryTypeOptions} />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'name']} label="Reader name">
                      <Input placeholder="Enter reader name" />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'visitorType']} label="Reader line">
                      <Select placeholder="Select reader line" options={visitorTypeOptions} allowClear />
                    </Form.Item>
                    <Button
                      htmlType="button"
                      style={{ width: '100%' }}
                      icon={<Trash className="trach-icon" />}
                      onClick={() => remove(name)}
                      danger>
                      Remove
                    </Button>
                  </div>
                ))}
                <Button htmlType="button" icon={<PlusOutlined />} onClick={() => add()} style={{ width: '100%' }}>
                  Add reader
                </Button>
              </>
            )}
          </Form.List>
        </div>

        <div className="edit-gate-form-list-wrapper">
          <h1 className="h1">Gate relays</h1>
          <Form.List name="relays">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div className="edit-gate-sub-list" key={key}>
                    <Form.Item {...restField} name={[name, 'number']} label="Relay number" rules={addEditRealyRules.number}>
                      <Input placeholder="Enter relay number" />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'name']} label="Relay name" rules={addEditRealyRules.name}>
                      <Input placeholder="Enter relay name" />
                    </Form.Item>
                    <Button
                      htmlType="button"
                      style={{ width: '100%' }}
                      icon={<Trash className="trach-icon" />}
                      onClick={() => remove(name)}
                      danger>
                      Remove
                    </Button>
                  </div>
                ))}
                <Button htmlType="button" icon={<PlusOutlined />} onClick={() => add()} style={{ width: '100%' }}>
                  Add relay
                </Button>
              </>
            )}
          </Form.List>
        </div>

        <Space className="footer">
          <Button type="link" onClick={() => hideModal(false)} className="eiq-button">
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} className="eiq-button">
            Save
          </Button>
        </Space>
      </FormWrapper>
    </Modal>
  );
});

export default React.memo(AddEditGateModal, (prevProps, nextProps) => {
  return prevProps.isOpen === nextProps.isOpen;
});

export const addEditRealyRules = {
  number: [{ required: true, message: 'Relay number is required!' }],
  name: [{ required: true, message: 'Relay name name is required!' }],
};

export const addEditGateRules = {
  cameraName: [{ required: true, message: 'Camera name is required!' }],
  cameraDirection: [{ required: true, message: 'Camera direction is required!' }],
  readerNumber: [{ required: true, message: 'Reader number name is required!' }],
  readerDirection: [{ required: true, message: 'Reader direction is required!' }],
  readerEntryType: [{ required: true, message: 'Reader entry type is required!' }],
};

export const visitorTypeOptions = [
  {
    label: 'Resident',
    value: 1,
  },
  {
    label: 'Visitor',
    value: 2,
  },
];
